import {useCallback, useState} from 'react'
import {getDiagnosisSelectionVersion, getFirstMappedCode} from '@/features/projects/diagnosisVersion'
import {Diagnosis, DiagnosisVersion} from '@/features/projects/types'

export interface DiagnosisCodesPair {
    icd9: Diagnosis | null
    icd10: Diagnosis | null
}

interface UseDiagnosisCodesLinkProps {
    icd9?: Diagnosis | null
    icd10?: Diagnosis | null
    /** Called with both codes at once: a linked pick changes the two fields in a single update. */
    onChange: (codes: DiagnosisCodesPair) => void
}

/**
 * Value to write in the other classification when one of the two codes changes.
 * - chain open: the counterpart is never touched, the two fields stay fully independent;
 * - code cleared: the chain propagates the clear;
 * - code with no transcoding: the counterpart is kept. The regional table does not map every code,
 *   and dropping an explicit choice because the picked code happens to be unmapped would lose data.
 */
export const getLinkedCounterpart = ({
    value,
    counterpart,
    isLinked
}: {
    value: Diagnosis | null
    counterpart: Diagnosis | null
    isLinked: boolean
}): Diagnosis | null => {
    if (!isLinked) return counterpart
    if (!value) return null
    return getFirstMappedCode(value) ?? counterpart
}

/**
 * Pair to write when the chain closes on two codes that were picked while it was open. The code
 * edited last drives and the other is transcoded from it — after "unlink, change the pathology, link
 * again" the two are expected to be a matching pair, not to stay apart until the next pick.
 *
 * If the driving code is empty the roles swap, so closing the chain fills the blank field instead of
 * emptying the filled one. Unlike an explicit clear, closing the chain never wipes a code: with no
 * transcoding for the driving one the pair is left exactly as it is.
 */
export const getAlignedCodes = ({
    icd9,
    icd10,
    lastEditedVersion
}: DiagnosisCodesPair & {lastEditedVersion: DiagnosisVersion}): DiagnosisCodesPair => {
    const isIcd10Driving = lastEditedVersion == 'ICD10' ? !!icd10 : !icd9
    return isIcd10Driving
        ? {icd9: getFirstMappedCode(icd10) ?? icd9, icd10}
        : {icd9, icd10: getFirstMappedCode(icd9) ?? icd10}
}

/**
 * Chain between the ICD-9 and ICD-10 fields of a single diagnosis. Closed, every pick rewrites the
 * other classification through the transcoding, in both directions; open, the two fields are
 * independent. It starts closed, so leaving it alone keeps the fill-in that was there before.
 *
 * Closing it again realigns the pair on the spot — see getAlignedCodes.
 *
 * From ICD10_MANDATORY_FROM the ICD-9 is no longer selectable and can only be filled from the
 * ICD-10, so the chain is locked closed.
 */
export const useDiagnosisCodesLink = ({icd9, icd10, onChange}: UseDiagnosisCodesLinkProps) => {
    const isIcd9Selectable = getDiagnosisSelectionVersion() == 'ICD9'
    const [isLinked, setIsLinked] = useState(true)
    // Which side drives when the chain closes. Starts on the classification in force: that is the one
    // a code loaded from the server was picked in, before anybody touched this form.
    const [lastEditedVersion, setLastEditedVersion] = useState<DiagnosisVersion>(getDiagnosisSelectionVersion())

    const handleSelectIcd9 = useCallback(
        (value: Diagnosis | null) => {
            setLastEditedVersion('ICD9')
            onChange({
                icd9: value,
                icd10: getLinkedCounterpart({value, counterpart: icd10 ?? null, isLinked})
            })
        },
        [icd10, isLinked, onChange]
    )

    const handleSelectIcd10 = useCallback(
        (value: Diagnosis | null) => {
            setLastEditedVersion('ICD10')
            onChange({
                icd9: getLinkedCounterpart({value, counterpart: icd9 ?? null, isLinked}),
                icd10: value
            })
        },
        [icd9, isLinked, onChange]
    )

    const onToggleLink = useCallback(
        (nextIsLinked: boolean) => {
            setIsLinked(nextIsLinked)
            if (!nextIsLinked) return

            const aligned = getAlignedCodes({icd9: icd9 ?? null, icd10: icd10 ?? null, lastEditedVersion})
            // Opening and closing the chain without changing anything must not touch the form: writing
            // the same pair back would mark it dirty and light up the save button for nothing.
            if (aligned.icd9?.id == icd9?.id && aligned.icd10?.id == icd10?.id) return
            onChange(aligned)
        },
        [icd9, icd10, lastEditedVersion, onChange]
    )

    // isIcd9Selectable drives both the ICD-9 select and the toggle: once the ICD-9 can no longer be
    // searched, unlinking would leave it unfillable.
    return {isLinked, isIcd9Selectable, onToggleLink, handleSelectIcd9, handleSelectIcd10}
}
